/* GuestIntelligenceTab — new vs repeat, loyalty mix, LTV per source, VIP list.
 * Stays-only (requires Absolute PMS guest profiles). */
import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Users, Crown, TrendingUp, Sparkles } from 'lucide-react';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const TIER_COLORS = { vip: '#A855F7', gold: '#F59E0B', silver: '#94A3B8', standard: '#6B7280' };

export default function GuestIntelligenceTab() {
  const [summary, setSummary] = useState(null);
  const [ltv, setLtv] = useState(null);
  const [vip, setVip] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, l, v] = await Promise.all([
          api.get('/outlet-intel/stays/guest/summary?days=30'),
          api.get('/outlet-intel/stays/guest/ltv-by-source'),
          api.get('/outlet-intel/stays/guest/vip-list?limit=15'),
        ]);
        if (cancelled) return;
        setSummary(s.data); setLtv(l.data); setVip(v.data);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-sm" style={{ color: '#6B7280' }}>Loading guest intelligence…</div>;

  return (
    <div className="space-y-4" data-testid="guest-intel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Guests (30d)" value={summary?.total_guests_in_window || 0} sub={`${summary?.avg_stays_per_guest || 0} avg stays / guest`} icon={Users} testid="kpi-total" />
        <Kpi label="Repeat guests" value={`${summary?.repeat_pct || 0}%`} sub={`${summary?.repeat_guests || 0} of ${summary?.total_guests_in_window || 0} — the loyal core`} icon={Sparkles} highlight testid="kpi-repeat" />
        <Kpi label="Avg revenue / guest" value={INR(summary?.avg_revenue_per_guest)} sub="Window-period contribution" icon={TrendingUp} testid="kpi-arpg" />
        <Kpi label="VIP tier" value={summary?.loyalty_mix?.vip || 0} sub={`${summary?.loyalty_mix?.gold || 0} Gold · ${summary?.loyalty_mix?.silver || 0} Silver`} icon={Crown} accent={TIER_COLORS.vip} testid="kpi-vip" />
      </div>

      {/* Loyalty mix bars */}
      <Card title="Loyalty Tier Mix" testid="card-loyalty">
        <div className="space-y-2">
          {Object.entries(summary?.loyalty_mix || {}).map(([tier, count]) => {
            const total = Object.values(summary?.loyalty_mix || {}).reduce((a, b) => a + b, 0);
            const pct = total ? (count / total) * 100 : 0;
            return (
              <div key={tier} className="flex items-center gap-3 text-[12px]">
                <span className="w-20 font-medium capitalize">{tier}</span>
                <span className="flex-1 rounded-full h-2.5" style={{ backgroundColor: '#F3F4F6' }}>
                  <span className="block h-2.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: TIER_COLORS[tier] || '#6B7280' }} />
                </span>
                <span className="w-12 text-right" style={{ color: '#6B7280' }}>{count}</span>
                <span className="w-12 text-right text-[10px]" style={{ color: '#9CA3AF' }}>{pct.toFixed(1)}%</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* LTV by source */}
      <Card title="Lifetime Value by Booking Source" testid="card-ltv-source">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr style={{ color: '#6B7280' }} className="text-[10px] uppercase">
                <th className="text-left py-2">Source</th>
                <th className="text-right">Guests</th>
                <th className="text-right">Stays</th>
                <th className="text-right">Gross Rev</th>
                <th className="text-right">Avg LTV</th>
              </tr>
            </thead>
            <tbody>
              {(ltv?.rows || []).map(r => (
                <tr key={r.source} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td className="py-1.5 capitalize">{r.source.replace('_', ' ')}</td>
                  <td className="text-right">{r.unique_guests}</td>
                  <td className="text-right">{r.total_stays}</td>
                  <td className="text-right font-semibold">{INR(r.gross_revenue)}</td>
                  <td className="text-right" style={{ color: r.avg_ltv > 20000 ? '#10B981' : '#6B7280' }}>{INR(r.avg_ltv)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* VIP list */}
      <Card title={`Top ${vip?.top_n || 15} Guests by Lifetime Revenue`} testid="card-vip">
        {(vip?.rows || []).map((g, i) => (
          <div key={g.guest_id} className="flex items-center gap-3 py-1.5 text-[12px]" style={{ borderTop: i ? '1px solid #F3F4F6' : 'none' }}>
            <span className="w-6 text-right" style={{ color: '#9CA3AF' }}>#{i + 1}</span>
            <span className="flex-1">
              <div className="font-medium">{g.name}</div>
              <div className="text-[10px]" style={{ color: '#9CA3AF' }}>{g.city || '—'} · last stay {g.last_stay}</div>
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full capitalize" style={{
              backgroundColor: (TIER_COLORS[g.loyalty_tier] || '#6B7280') + '22',
              color: TIER_COLORS[g.loyalty_tier] || '#6B7280',
            }}>{g.loyalty_tier}</span>
            <span className="w-12 text-right text-[10px]" style={{ color: '#6B7280' }}>{g.stays} stays</span>
            <span className="w-24 text-right font-semibold">{INR(g.lifetime_revenue)}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, highlight, accent, icon: Icon, testid }) {
  const bg = highlight ? '#1B2A4A' : 'white';
  const fg = highlight ? 'white' : '#1B2A4A';
  const muted = highlight ? '#9DB5CC' : '#6B7280';
  return (
    <div className="rounded-xl p-4" style={{
      backgroundColor: bg, color: fg,
      border: highlight ? 'none' : '1px solid #E5E1DC',
      borderLeft: accent ? `4px solid ${accent}` : undefined,
    }} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={12} color={muted} />}
        <p className="text-[10px] uppercase tracking-wider" style={{ color: muted }}>{label}</p>
      </div>
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-[11px] mt-1" style={{ color: muted }}>{sub}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl bg-white p-5" style={{ border: '1px solid #E5E1DC' }} data-testid={testid}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>{title}</h3>
      {children}
    </div>
  );
}
